/* eslint-disable react-hooks/exhaustive-deps */
import React, {useMemo, useState} from "react"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {useMutation, useQuery} from "@tanstack/react-query"
import {Col, Row, Tooltip, Button} from "antd"
import cx from "classnames"
import {Icon} from "components/Icon"
import {BaseButton} from "components/buttons/Button/BaseButton"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import ProgramSelect from "components/ProgramSelect"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import {BaseRangePicker} from "components/DateTimePicker"
import CampusSelect from "components/CampusSelect"
import {BaseInput} from "components/inputs"
import {ExportButton, ExportFileType} from "components/ui"
import {KlassappTable, KlassappTableHeader, BaseNewFilter, SignaturePopup} from "uiKit"
import {KlassappTableProps, Order} from "types/common"
import {FilterKey} from "types/filter"
import {StudentStatus} from "types/students"
import {exportCsv, exportExcel} from "helpers/export-table"
import {formatDateOnly, formatDecimal, getFullName, handleError, toastInfo} from "helpers"
import {useModel, useVisible} from "hooks"
import {useCurrentProfilePermissions} from "hooks/useCurrentProfilePermissions"
import {useFilterStorage} from "hooks/useFilterStorage"
import {reportsService, sapCheckpointsService, webFormService} from "services"
import {KlassappTableHOC} from "HOC"
import {sapCheckpointKindOptions, SapCheckpointKind} from "types/settings/sap-checkpoint"
import {
    ListSapCheckpointChecksPayload,
    SapCheckpointCheck,
    UpdateSapCheckpointChecksPayload
} from "types/settings/sap-checkpoint-check"
import {Permissions} from "types/permission"
import RunSapChecksPopup from "./RunSapChecksPopup"
import SapChecksStats from "./SapChecksStats"
import styles from "./SapChecksList.module.css"
import {BaseDepartmentId} from "types/departments"

type SapMaintenanceStatus = "maintaining" | "not_maintaining"
type SapCheckReviewStatus = "pending" | "approved" | "rejected"

const SAP_CHECK_EDIT_PERMISSIONS = {
    staff: [
        Permissions.Staff.Academics.Instructional.Students.AcademicsStudentDetails.Edit,
        Permissions.Staff.Academics.Registrar.Students.WorkspaceStudent.Edit,
        Permissions.Staff.StudentServicesAndAcademicAffairs.StudentServices.Students.StudentDetail.Edit,
        Permissions.Staff.AcademicAffairs.StudentServices.Students.StudentDetail.Edit,
        Permissions.Staff.CareerServices.Edit,
        Permissions.Staff.Settings.General.Sap.Edit
    ]
}

const SAP_DOCUMENT_DEPARTMENT_ID: number = BaseDepartmentId.Academics
const SAP_DOCUMENT_DEPARTMENT_SUBUNIT_ID: number = BaseDepartmentId.Academics
const MAX_SAP_REPORT_CHECKS = 100

type SapChecksListFilter = {
    programs: Array<{id: number}>
    programVersions: Array<{id: number}>
    sapKinds: Array<{id: SapCheckpointKind; name: string}>
    sapStatuses: Array<{id: SapMaintenanceStatus; name: string}>
    studentStatuses: StudentStatus[]
    campuses: Array<{id: number}>
    dateHitFrom: string | null
    dateHitTo: string | null
    checkpointHoursFrom: string
    checkpointHoursTo: string
}

const DEFAULT_FILTER: SapChecksListFilter = {
    programs: [],
    programVersions: [],
    sapKinds: [],
    sapStatuses: [],
    studentStatuses: [],
    campuses: [],
    dateHitFrom: null,
    dateHitTo: null,
    checkpointHoursFrom: "",
    checkpointHoursTo: ""
}

function formatHours(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ""
    }
    return String(Number(value))
}

function overallStatNumber(item: SapCheckpointCheck, key: string) {
    const value = item.jsonData?.overallStats?.[key]
    if (value === null || value === undefined || value === "") {
        return null
    }
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}

function formatTimeframePercentage(item: SapCheckpointCheck) {
    const hoursScheduled = overallStatNumber(item, "hoursScheduled")
    const completedHours = Number(item.completedHours)
    if (hoursScheduled == null || !Number.isFinite(completedHours) || completedHours === 0) {
        return ""
    }
    return `${formatDecimal((hoursScheduled / completedHours) * 100)}%`
}

function formatCgpa(item: SapCheckpointCheck) {
    const cgpa = overallStatNumber(item, "cgpa")
    if (cgpa == null) {
        return ""
    }
    return formatDecimal(cgpa)
}

function formatCheckedAt(value: string | null | undefined, dateFormat: string) {
    if (!value) {
        return ""
    }
    return moment(value).format(`${dateFormat} hh:mm A`)
}

type MaintenanceRule = "cgpa" | "timeframe" | "attendance"

function ruleComparison(item: SapCheckpointCheck, rule: MaintenanceRule) {
    return item.sapSettings?.[rule]
}

function formatComparisonValue(value: number | null | undefined, rule: MaintenanceRule) {
    if (value == null || !Number.isFinite(Number(value))) {
        return null
    }
    const numeric = Number(value)
    const label = Number.isInteger(numeric) ? String(numeric) : formatDecimal(numeric)
    return rule === "cgpa" ? label : `${label}%`
}

function displayValue(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === "") {
        return ""
    }
    return String(value)
}

function idsOf(items: Array<{id: number}> | undefined) {
    return (items ?? []).map((item) => item.id).filter((id) => Number.isFinite(id))
}

function parseHoursBound(value: string | undefined) {
    if (value == null || value.trim() === "") {
        return null
    }
    const hours = Number(value)
    return Number.isFinite(hours) ? hours : null
}

function toListSort(orderField?: {field?: string; order?: string}): {
    orderBy: string
    orderDir: "ASC" | "DESC"
} {
    return {
        orderBy: orderField?.field || "lastName",
        orderDir: orderField?.order === Order.Desc ? "DESC" : "ASC"
    }
}

type SapChecksListProps = KlassappTableProps & {
    isReport?: boolean
    studentProfileId?: number
}

function SapChecksList(props: SapChecksListProps) {
    const {isReport = false, studentProfileId} = props
    const isStudentDetail = studentProfileId != null && Number.isFinite(studentProfileId)
    const filterMemoryKey = isStudentDetail
        ? FilterKey.StudentSapChecks
        : isReport
        ? FilterKey.SapChecksReport
        : FilterKey.SapChecks
    const {
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        orderField,
        dispatch,
        dispatchFunc,
        tableRef,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        isLoadedTableFuncs
    } = props
    const {t} = useTranslation(["academics", "user", "common"])
    const model = useModel()
    const currentProfilePermissions = useCurrentProfilePermissions()
    const hasEditPermission = currentProfilePermissions.hasPermissions(SAP_CHECK_EDIT_PERMISSIONS)
    const dateFormat = model.getUserDateFormat()
    const [rerunningId, setRerunningId] = useState<number | null>(null)
    const [isExporting, setIsExporting] = useState(false)
    const [isGeneratingReport, setIsGeneratingReport] = useState(false)
    const [isRunSapPopupVisible, setIsRunSapPopupVisible] = useState(false)
    const [reviewItem, setReviewItem] = useState<SapCheckpointCheck | null>(null)
    const [isApproving, setIsApproving] = useState(false)
    const [isRejecting, setIsRejecting] = useState(false)
    const signaturePopup = useVisible(false)
    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        filterMemoryKey,
        DEFAULT_FILTER
    )
    const [appliedFilter, setAppliedFilter] = useState<SapChecksListFilter>(currentFilter)
    const [appliedSearch, setAppliedSearch] = useState(searchValue || "")
    const selectedChecks: SapCheckpointCheck[] = (data ?? []).filter((item) => item.isChecked)
    const activeReportGenerated = selectedChecks.length
        ? selectedChecks[0].jsonData?.isReportGenerated === true
        : undefined
    const reportableChecks = selectedChecks.filter(
        (item) => item.jsonData?.status === "maintaining" || item.jsonData?.status === "not_maintaining"
    )
    const selectedSapCheckpointCheckIds = Array.from(new Set(reportableChecks.map((item) => item.sapCheckpointCheckId)))
    const selectedDocumentCount = reportableChecks.filter((item) => item.jsonData.status === "maintaining").length
    const selectedTaskCount = reportableChecks.length - selectedDocumentCount

    // Product explicitly requested select-all to target pending checks by default, or the active group when selected.
    const onToggleCheckHeader = (checked: boolean) => {
        const targetReportGenerated = activeReportGenerated ?? false
        onUpdateTableData(
            (data ?? []).map((item) =>
                !item.isCheckboxDisabled && (item.jsonData?.isReportGenerated === true) === targetReportGenerated
                    ? {...item, isChecked: checked}
                    : item
            )
        )
    }

    async function generateSapCheckReports(sapCheckpointCheckIds: number[]) {
        if (isGeneratingReport) return
        try {
            if (
                !sapCheckpointCheckIds.length ||
                sapCheckpointCheckIds.length > MAX_SAP_REPORT_CHECKS ||
                sapCheckpointCheckIds.some((id) => !Number.isInteger(id) || id <= 0)
            ) {
                throw new Error(t("sapReports.invalidChecks"))
            }
            const checksToUpdate: UpdateSapCheckpointChecksPayload = sapCheckpointCheckIds.map((id) => {
                const check: SapCheckpointCheck = (data ?? []).find((item) => item.sapCheckpointCheckId === id)
                const status = check?.jsonData?.status
                if (status !== "maintaining" && status !== "not_maintaining") {
                    throw new Error(t("sapReports.invalidStatus"))
                }
                return {
                    sapCheckpointCheckId: id,
                    jsonData: {
                        isReportGenerated: true,
                        reportType: status === "maintaining" ? "document" : "task"
                    }
                }
            })
            setIsGeneratingReport(true)
            onUpdateTableData((data ?? []).map((item) => ({...item, isChecked: false})))
            const result = await webFormService.generateSapDocuments({
                slug: model.slug,
                sapCheckpointCheckIds,
                departmentId: SAP_DOCUMENT_DEPARTMENT_ID,
                departmentSubunitId: SAP_DOCUMENT_DEPARTMENT_SUBUNIT_ID
            })
            if (!result.success) {
                throw new Error(t("sapReports.failed"))
            }
            toastInfo(t("sapReports.queued"))
            // By design, mark on batch acceptance to discourage repeated generation requests while processing.
            // This means generation was requested, not confirmed; web-forms returns no per-item IDs or confirmation.
            // A later background failure may leave the check marked, so regeneration remains available.
            await sapCheckpointsService.updateChecks(checksToUpdate)
            await checksQuery.refetch()
        } catch (error) {
            handleError(error)
        } finally {
            setIsGeneratingReport(false)
        }
    }

    const sapStatusOptions = useMemo(
        () =>
            (["maintaining", "not_maintaining"] as SapMaintenanceStatus[]).map((status) => ({
                id: status,
                name: t(`academics.sapChecks.sapStatus.${status}`)
            })),
        [t]
    )

    const sapKindOptions = useMemo(
        () =>
            sapCheckpointKindOptions.map((kind) => ({
                id: kind.id,
                name: t(`academics.sapChecks.kind.${kind.id}`)
            })),
        [t]
    )

    const getSapKindLabel = (sapKind: SapCheckpointCheck["sapKind"]) => {
        const option = sapKindOptions.find((kind) => kind.id === sapKind)
        return option ? option.name : sapKind
    }

    const getStatusLabel = (item: SapCheckpointCheck) => {
        const statusCode = item.jsonData?.status
        if (!statusCode) {
            return ""
        }
        return t(`academics.sapChecks.sapStatus.${statusCode}`, {defaultValue: statusCode})
    }

    const getReviewStatusLabel = (status: SapCheckReviewStatus | undefined) => {
        if (status === "approved") {
            return t("academics.sapChecks.reviewApproved")
        }
        if (status === "rejected") {
            return t("academics.sapChecks.reviewRejected")
        }
        return t("academics.sapChecks.reviewPending")
    }

    const onClickReview = (item: SapCheckpointCheck) => {
        if (!hasEditPermission || item.approvalStatus !== "pending") {
            return
        }
        setReviewItem(item)
        signaturePopup.open()
    }

    const renderReviewStatus = (item: SapCheckpointCheck) => {
        const status = item.approvalStatus ?? "pending"
        if (status === "pending") {
            return (
                <div
                    className={hasEditPermission ? styles.review : styles.reviewDisabled}
                    onClick={(event) => {
                        event.stopPropagation()
                        onClickReview(item)
                    }}>
                    {t("academics.sapChecks.review")}
                </div>
            )
        }
        const signature = item.approvalSignature
        const isApproved = status === "approved"
        return (
            <div className={styles.statusWrap}>
                <div
                    className={cx(
                        styles.signatureWrap,
                        isApproved ? styles.signatureApproval : styles.signatureReject
                    )}>
                    <Icon
                        icon={isApproved ? "CHECKMARK_CIRCLE" : "CROSS_CIRCLE"}
                        className={styles.signatureStatusIcon}
                        color={isApproved ? "#18A957" : "#DF1642"}
                    />
                    {signature?.imageUrl ? (
                        <img src={signature.imageUrl} alt="" className={styles.signatureImg} />
                    ) : null}
                </div>
                <div className={styles.dateInfo}>
                    <p className={styles.dateInfoText}>{formatCheckedAt(item.approvalStatusUpdatedAt, dateFormat)}</p>
                    <p className={styles.dateInfoText}>
                        {signature ? getFullName(signature) : t("academics.sapChecks.reviewedBySystem")}
                    </p>
                </div>
            </div>
        )
    }

    const renderMeasuredCell = (value: string, rule: MaintenanceRule, item: SapCheckpointCheck) => {
        const comparison = ruleComparison(item, rule)
        const unmet = comparison ? !comparison.met : (item.jsonData?.unmetRules ?? []).includes(rule)
        if (!unmet) {
            return value
        }
        const currentLabel =
            formatComparisonValue(comparison?.value, rule) ?? (value || t("academics.sapChecks.compare.empty"))
        const settingLabel =
            comparison?.setting == null
                ? t("academics.sapChecks.compare.noSetting")
                : formatComparisonValue(comparison.setting, rule)
        return (
            <Tooltip
                title={
                    <div className={styles.compareTip}>
                        <div>
                            {t("academics.sapChecks.compare.current")}: {currentLabel}
                        </div>
                        <div>
                            {t("academics.sapChecks.compare.setting", {
                                name: t(`academics.sapChecks.compare.${rule}`)
                            })}
                            : {settingLabel}
                        </div>
                    </div>
                }>
                <span className={styles.unmetCell}>
                    <Icon icon="WARNING_TRIANGLE_FILL" color="#ff8a38" size={16} className={styles.unmetIcon} />
                    <span>{value || t("academics.sapChecks.compare.empty")}</span>
                </span>
            </Tooltip>
        )
    }

    const decorateItem = (item: SapCheckpointCheck) => {
        const versionName = item.programVersion?.versionName
        const versionCode = item.programVersion?.versionCode
        const programVersion = versionCode ? `${versionName} (${versionCode})` : versionName
        return {
            ...item,
            id: item.sapCheckpointCheckId,
            firstName: item.student?.firstName || "",
            lastName: item.student?.lastName || "",
            recordTypeLabel: item.isRerun ? t("academics.sapChecks.rerunRecord") : t("academics.sapChecks.check"),
            sapKindLabel: getSapKindLabel(item.sapKind),
            incrementNumberLabel: displayValue(item.jsonData?.incrementNumber || null),
            dateRunLabel: formatDateOnly(item.jsonData?.dateRun, dateFormat),
            dateHitLabel: formatDateOnly(item.jsonData?.dateHit, dateFormat),
            statusLabel: getStatusLabel(item),
            attendancePercentageLabel: displayValue(item.jsonData?.attendancePercentage),
            gradeLabel: displayValue(item.jsonData?.grade),
            programName: item.program?.name || "",
            programVersionName: programVersion || "",
            shiftNames: (item.shifts ?? [])
                .map((shift) => shift.name)
                .filter(Boolean)
                .join(", "),
            checkpointHoursLabel: formatHours(item.checkpointHours),
            completedHoursLabel: formatHours(item.completedHours),
            scheduledHoursLabel: formatHours(overallStatNumber(item, "hoursScheduled")),
            timeframePercentageLabel: formatTimeframePercentage(item),
            cgpaLabel: formatCgpa(item),
            checkedAtLabel: formatCheckedAt(item.checkedAt, dateFormat),
            rerunResultLabel: item.rerunResult ? t(`academics.sapChecks.result.${item.rerunResult}`) : "",
            customUserId: item.customUserId || "",
            enrollmentId: item.enrollmentId || "",
            startingTermName: item.startingTermName || "",
            studentStatusName: item.studentStatusName || "",
            campusNames: (item.campusNames ?? []).filter(Boolean).join(", "),
            currentCompletedHoursLabel: formatHours(item.currentCompletedHours),
            ldaLabel: formatDateOnly(item.lda, dateFormat),
            reviewStatusLabel: getReviewStatusLabel(item.approvalStatus)
        }
    }

    const buildFilter = (filter: SapChecksListFilter, search = ""): ListSapCheckpointChecksPayload["filter"] => {
        const programIds = idsOf(filter.programs)
        const programVersionIds = idsOf(filter.programVersions)
        const sapKinds = (filter.sapKinds ?? []).map((kind) => kind.id)
        const sapStatuses = (filter.sapStatuses ?? []).map((status) => status.id)
        const studentStatusIds = (filter.studentStatuses ?? [])
            .map((status) => status.statusId)
            .filter((id) => Number.isFinite(id))
        const campusIds = idsOf(filter.campuses)
        const checkpointHoursFrom = parseHoursBound(filter.checkpointHoursFrom)
        const checkpointHoursTo = parseHoursBound(filter.checkpointHoursTo)
        const term = search.trim()
        return {
            ...(typeof studentProfileId === "number" ? {studentProfileIds: [studentProfileId]} : {}),
            ...(programIds.length ? {programIds} : {}),
            ...(programVersionIds.length ? {programVersionIds} : {}),
            ...(sapKinds.length ? {sapKinds} : {}),
            ...(sapStatuses.length ? {sapStatuses} : {}),
            ...(studentStatusIds.length ? {studentStatusIds} : {}),
            ...(campusIds.length ? {campusIds} : {}),
            ...(filter.dateHitFrom ? {dateHitFrom: filter.dateHitFrom} : {}),
            ...(filter.dateHitTo ? {dateHitTo: filter.dateHitTo} : {}),
            ...(checkpointHoursFrom == null ? {} : {checkpointHoursFrom}),
            ...(checkpointHoursTo == null ? {} : {checkpointHoursTo}),
            ...(term ? {search: term} : {})
        }
    }

    const listSort = toListSort(orderField)

    const checksQuery = useQuery(
        [
            "sap-checkpoint-checks-all",
            isReport,
            studentProfileId ?? null,
            page,
            pageSize,
            appliedFilter,
            appliedSearch,
            listSort
        ],
        async () => {
            const payload = {
                filter: buildFilter(appliedFilter, appliedSearch),
                range: {page, pageSize},
                sort: listSort,
                linkedEntities: true
            }
            const {data: checks, total: checksTotal} = await sapCheckpointsService.listChecks(payload)
            return {data: checks.map(decorateItem), total: checksTotal}
        },
        {
            enabled: isLoadedTableFuncs && (typeof studentProfileId !== "number" || studentProfileId > 0),
            keepPreviousData: true,
            onSuccess: (result) => {
                dispatch({data: result.data, total: result.total})
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const submitReview = async (approvalStatus: Exclude<SapCheckReviewStatus, "pending">) => {
        if (!reviewItem || isApproving || isRejecting) {
            return
        }
        const setLoading = approvalStatus === "approved" ? setIsApproving : setIsRejecting
        try {
            setLoading(true)
            await sapCheckpointsService.reviewCheck({
                sapCheckpointCheckId: reviewItem.sapCheckpointCheckId,
                approvalStatus
            })
            setReviewItem(null)
            signaturePopup.close()
            await checksQuery.refetch()
        } catch (error) {
            handleError(error)
        } finally {
            setLoading(false)
        }
    }

    const rerunMutation = useMutation(
        (sapCheckpointCheckId: number) => sapCheckpointsService.rerunCheck(sapCheckpointCheckId),
        {
            onSuccess: () => {
                checksQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            },
            onSettled: () => {
                setRerunningId(null)
            }
        }
    )

    const onRerun = (sapCheckpointCheckId: number) => {
        if (rerunMutation.isLoading) {
            return
        }
        setRerunningId(sapCheckpointCheckId)
        rerunMutation.mutate(sapCheckpointCheckId)
    }

    const appliedChecksFilter = buildFilter(appliedFilter)
    const listFilter = buildFilter(appliedFilter, appliedSearch)

    const onClickExportBtn = async (type: ExportFileType) => {
        try {
            setIsExporting(true)
            const reportColumns = columns.filter(
                (column) =>
                    fields.includes(column.title) && column.field !== "rerunHtml" && column.field !== "sapReportHtml"
            )
            if (isReport && total > 1000) {
                await reportsService.generateReport({
                    reportKey: "sapChecksReport",
                    payload: {
                        filter: listFilter,
                        fields: reportColumns.map((column) => ({
                            field: column.field,
                            title: column.title
                        }))
                    },
                    format: type
                })
                toastInfo(t("academics.sapChecks.reportQueued"))
                return
            }
            const exportPayload = {
                filter: listFilter,
                range: {page: 1, pageSize: total},
                sort: listSort,
                linkedEntities: true
            }
            const checks = total ? (await sapCheckpointsService.listChecks(exportPayload)).data : []
            const payload = {
                filename: `${isReport ? "Sap-Checks-Report" : "Sap-Checks"}-${moment().format("MM_DD_YYYY")}`,
                columns: reportColumns.map((column) => column.title.toUpperCase()),
                rows: checks.map((item) => {
                    const decorated = decorateItem(item) as Record<string, unknown>
                    return reportColumns.map((column) => {
                        const value = decorated[column.field]
                        return value == null ? "" : String(value).replace(/\r\n|\n|\r/g, " ")
                    })
                })
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (error) {
            handleError(error)
        } finally {
            setIsExporting(false)
        }
    }

    const getFields = () => [
        t("user:firstName"),
        t("user:lastName"),
        t("user:userId"),
        t("academics.sapChecks.enrollmentId"),
        t("academics.sapChecks.startingTerm"),
        t("academics.sapChecks.filters.studentStatus"),
        t("academics.sapChecks.campus"),
        t("academics.sapChecks.program"),
        t("academics.sapChecks.programVersion"),
        t("academics.sapChecks.shifts"),
        t("academics.sapChecks.currentCompletedHours"),
        t("academics.sapChecks.lda"),
        t("academics.sapChecks.recordType"),
        t("academics.sapChecks.sapKind"),
        t("academics.sapChecks.status"),
        t("academics.sapChecks.attendancePercentage"),
        t("academics.sapChecks.grade"),
        t("academics.sapChecks.incrementNumber"),
        t("academics.sapChecks.dateRun"),
        t("academics.sapChecks.dateHit"),
        t("academics.sapChecks.checkpointHours"),
        t("academics.sapChecks.completedHours"),
        t("academics.sapChecks.scheduledHour"),
        t("academics.sapChecks.timeframePercentage"),
        t("academics.sapChecks.cgpa"),
        t("academics.sapChecks.checkedAt"),
        t("academics.sapChecks.resultTitle"),
        t("academics.sapChecks.review"),
        t("academics.sapChecks.rerun"),
        t("common:action.report")
    ]

    const getFilterMemoryKey = () => filterMemoryKey

    const getColumns = () => [
        {
            title: t("user:firstName"),
            field: "firstName",
            columnIndex: 1,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("user:lastName"),
            field: "lastName",
            columnIndex: 2,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("user:userId"),
            field: "customUserId",
            columnIndex: 3,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.enrollmentId"),
            field: "enrollmentId",
            columnIndex: 4,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.startingTerm"),
            field: "startingTermName",
            columnIndex: 5,
            sortable: true,
            style: {minWidth: "150px"}
        },
        {
            title: t("academics.sapChecks.filters.studentStatus"),
            field: "studentStatusName",
            columnIndex: 6,
            sortable: true,
            style: {minWidth: "150px"}
        },
        {
            title: t("academics.sapChecks.campus"),
            field: "campusNames",
            columnIndex: 7,
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.program"),
            field: "programName",
            columnIndex: 8,
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.programVersion"),
            field: "programVersionName",
            columnIndex: 9,
            sortable: true,
            style: {minWidth: "180px"}
        },
        {
            title: t("academics.sapChecks.shifts"),
            field: "shiftNames",
            columnIndex: 10,
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.currentCompletedHours"),
            field: "currentCompletedHoursLabel",
            columnIndex: 11,
            orderField: "currentCompletedHours",
            sortable: true,
            style: {minWidth: "180px"}
        },
        {
            title: t("academics.sapChecks.lda"),
            field: "ldaLabel",
            columnIndex: 12,
            orderField: "lda",
            sortable: true,
            style: {minWidth: "130px"}
        },
        {
            title: t("academics.sapChecks.recordType"),
            field: "recordTypeLabel",
            columnIndex: 13,
            orderField: "recordType",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.sapKind"),
            field: "sapKindLabel",
            columnIndex: 14,
            orderField: "sapKind",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.status"),
            field: "statusLabel",
            columnIndex: 15,
            orderField: "status",
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.cgpa"),
            field: "cgpaLabel",
            columnIndex: 28,
            style: {minWidth: "120px"},
            render: (value, row) => renderMeasuredCell(value ?? "", "cgpa", row)
        },
        {
            title: t("academics.sapChecks.timeframePercentage"),
            field: "timeframePercentageLabel",
            columnIndex: 27,
            style: {minWidth: "180px"},
            render: (value, row) => renderMeasuredCell(value ?? "", "timeframe", row)
        },
        {
            title: t("academics.sapChecks.attendancePercentage"),
            field: "attendancePercentageLabel",
            columnIndex: 16,
            orderField: "attendancePercentage",
            sortable: true,
            style: {minWidth: "150px"},
            render: (value, row) => renderMeasuredCell(value ?? "", "attendance", row)
        },
        {
            title: t("academics.sapChecks.grade"),
            field: "gradeLabel",
            columnIndex: 17,
            orderField: "grade",
            sortable: true,
            style: {minWidth: "100px"}
        },
        {
            title: t("academics.sapChecks.incrementNumber"),
            field: "incrementNumberLabel",
            columnIndex: 18,
            orderField: "incrementNumber",
            sortable: true,
            style: {minWidth: "110px"}
        },
        {
            title: t("academics.sapChecks.dateRun"),
            field: "dateRunLabel",
            columnIndex: 19,
            orderField: "dateRun",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.dateHit"),
            field: "dateHitLabel",
            columnIndex: 20,
            orderField: "dateHit",
            sortable: true,
            style: {minWidth: "120px"}
        },
        {
            title: t("academics.sapChecks.checkpointHours"),
            field: "checkpointHoursLabel",
            columnIndex: 21,
            orderField: "checkpointHours",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.completedHours"),
            field: "completedHoursLabel",
            columnIndex: 22,
            orderField: "completedHours",
            sortable: true,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.scheduledHour"),
            field: "scheduledHoursLabel",
            columnIndex: 26,
            style: {minWidth: "140px"}
        },
        {
            title: t("academics.sapChecks.checkedAt"),
            field: "checkedAtLabel",
            columnIndex: 23,
            orderField: "checkedAt",
            sortable: true,
            style: {minWidth: "170px"}
        },
        {
            title: t("academics.sapChecks.resultTitle"),
            field: "rerunResultLabel",
            columnIndex: 24,
            orderField: "rerunResult",
            sortable: true,
            style: {minWidth: "160px"}
        },
        {
            title: t("academics.sapChecks.review"),
            field: "reviewStatusLabel",
            columnIndex: 29,
            orderField: "approvalStatus",
            sortable: true,
            style: {minWidth: "220px"}
        },
        {
            title: t("academics.sapChecks.rerun"),
            field: "rerunHtml",
            columnIndex: 25,
            style: {minWidth: "120px"}
        },
        {title: t("common:action.report"), field: "sapReportHtml", style: {minWidth: "120px"}}
    ]

    React.useEffect(() => {
        if (!isStudentDetail || !isLoadedTableFuncs) {
            return
        }
        dispatch({page: 1})
    }, [studentProfileId, isLoadedTableFuncs])

    React.useEffect(() => {
        dispatch({
            isClassComponent: false,
            orderField: isStudentDetail
                ? {field: "checkedAt", order: Order.Desc}
                : {field: "lastName", order: Order.Asc}
        })
        dispatchFunc([
            {
                key: "getPageTitle",
                func: () => t(isReport ? "academics.sapChecks.reportTitle" : "academics.sapChecks.tab")
            },
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: checksQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: () => []}
        ])
    }, [])

    const onChangeFilter = <Key extends keyof SapChecksListFilter>(key: Key, value: SapChecksListFilter[Key]) => {
        const newFilter: SapChecksListFilter = {
            ...DEFAULT_FILTER,
            ...currentFilter,
            [key]: value
        }
        if (key === "programs") {
            newFilter.programVersions = []
        }
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        const nextFilter: SapChecksListFilter = {...DEFAULT_FILTER, ...currentFilter}
        applyFilter()
        setAppliedFilter(nextFilter)
        dispatch({page: 1})
    }

    const onSearchInput = (value: string) => {
        changeSearch(value)
        setAppliedSearch(value || "")
        dispatch({page: 1})
    }

    const programIds = idsOf(currentFilter.programs)
    const rows = (data ?? []).map((item) => ({
        ...item,
        reviewStatusLabel: renderReviewStatus(item),
        isCheckboxLocked:
            activeReportGenerated !== undefined &&
            (item.jsonData?.isReportGenerated === true) !== activeReportGenerated,
        sapReportHtml: (
            <div className={styles.action}>
                {/* The check indicates an accepted generation request, not confirmed completion; regeneration is allowed. */}
                {item.jsonData?.isReportGenerated === true && (
                    <Tooltip
                        title={
                            item.jsonData.reportType
                                ? t(`sapReports.generatedAs.${item.jsonData.reportType}`)
                                : t("sapReports.generated")
                        }>
                        <span aria-label={t("sapReports.generated")}>
                            <Icon icon="CHECKMARK_CIRCLE" color="#18a957" />
                        </span>
                    </Tooltip>
                )}
                <BaseButton
                    title={t(
                        item.jsonData?.isReportGenerated === true ? "sapReports.regenerate" : "sapReports.generate"
                    )}
                    variant="secondary"
                    uppercase={false}
                    className={styles.rerunButton}
                    loading={isGeneratingReport}
                    disabled={
                        isGeneratingReport ||
                        checksQuery.isFetching ||
                        (item.jsonData?.status !== "maintaining" && item.jsonData?.status !== "not_maintaining")
                    }
                    onClick={(event) => {
                        event.stopPropagation()
                        generateSapCheckReports([item.sapCheckpointCheckId])
                    }}
                />
            </div>
        ),
        rerunHtml:
            item.rerunCount > 0 ? (
                <span>{t("academics.sapChecks.alreadyRerun")}</span>
            ) : (
                <div className={styles.action}>
                    <BaseButton
                        title={t("academics.sapChecks.rerun")}
                        variant="secondary"
                        uppercase={false}
                        className={styles.rerunButton}
                        loading={rerunningId === item.sapCheckpointCheckId}
                        disabled={rerunMutation.isLoading}
                        onClick={(event) => {
                            event.stopPropagation()
                            onRerun(item.sapCheckpointCheckId)
                        }}
                    />
                </div>
            )
    }))

    return (
        <div>
            {isReport || isStudentDetail ? null : <SapChecksStats filter={appliedChecksFilter} />}
            <BaseNewFilter
                filter={currentFilter}
                onClick={onApplyFilter}
                onClickClear={clearFilter}
                searchValue={searchValue}
                onSearchInput={onSearchInput}
                renderRightFilter={() => (
                    <div className={styles.actionWrap}>
                        {isReport || isStudentDetail ? null : (
                            <BaseButton
                                title={t("academics.sapChecks.runSap")}
                                onClick={() => setIsRunSapPopupVisible(true)}
                            />
                        )}
                        <ExportButton
                            onSelect={onClickExportBtn}
                            isLoading={isExporting}
                            availableFileTypes={["csv", "excel"]}
                        />
                    </div>
                )}>
                <Row gutter={[12, 12]}>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.programs")} />
                        <ProgramSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.programs")}
                            value={currentFilter.programs}
                            onChange={(programs) => onChangeFilter("programs", programs)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.programVersions")} />
                        <ProgramVersionSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.programVersions")}
                            programId={programIds.length ? programIds : undefined}
                            value={currentFilter.programVersions}
                            onChange={(programVersions) => onChangeFilter("programVersions", programVersions)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.sapKinds")} />
                        <KlassDropdown
                            isMulti
                            isClearable
                            options={sapKindOptions}
                            valueKey="id"
                            labelKey="name"
                            placeholder={t("academics.sapChecks.filters.sapKinds")}
                            value={currentFilter.sapKinds}
                            onChange={(sapKinds) => onChangeFilter("sapKinds", sapKinds)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.studentStatus")} />
                        <NewStudentStatusSelectFilter
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.studentStatus")}
                            value={currentFilter.studentStatuses}
                            onChange={(studentStatuses) => onChangeFilter("studentStatuses", studentStatuses)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.campuses")} />
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder={t("academics.sapChecks.filters.campuses")}
                            value={currentFilter.campuses}
                            onChange={(campuses) => onChangeFilter("campuses", campuses ?? [])}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.dateHit")} />
                        <BaseRangePicker
                            dateOnly
                            allowEmpty={[true, true]}
                            placeholder={[
                                t("academics.sapChecks.filters.dateFrom"),
                                t("academics.sapChecks.filters.dateTo")
                            ]}
                            value={
                                currentFilter.dateHitFrom || currentFilter.dateHitTo
                                    ? [currentFilter.dateHitFrom, currentFilter.dateHitTo]
                                    : null
                            }
                            onChange={(range) => {
                                setCurrentFilter({
                                    ...DEFAULT_FILTER,
                                    ...currentFilter,
                                    dateHitFrom: range?.[0] || null,
                                    dateHitTo: range?.[1] || null
                                })
                            }}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.checkpointHoursFrom")} />
                        <BaseInput
                            type="number"
                            placeholder={t("academics.sapChecks.filters.checkpointHoursFrom")}
                            value={currentFilter.checkpointHoursFrom}
                            onChange={(checkpointHoursFrom) =>
                                onChangeFilter("checkpointHoursFrom", checkpointHoursFrom)
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.checkpointHoursTo")} />
                        <BaseInput
                            type="number"
                            placeholder={t("academics.sapChecks.filters.checkpointHoursTo")}
                            value={currentFilter.checkpointHoursTo}
                            onChange={(checkpointHoursTo) => onChangeFilter("checkpointHoursTo", checkpointHoursTo)}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label={t("academics.sapChecks.filters.sapStatus")} />
                        <KlassDropdown
                            isMulti
                            isClearable
                            options={sapStatusOptions}
                            valueKey="id"
                            labelKey="name"
                            placeholder={t("academics.sapChecks.filters.sapStatus")}
                            value={currentFilter.sapStatuses ?? []}
                            onChange={(sapStatuses) => onChangeFilter("sapStatuses", sapStatuses ?? [])}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <div className={styles.marginTop}>
                <KlassappTableHeader
                    isShowAction={selectedChecks.length > 0}
                    actions={[
                        {
                            title: t(
                                activeReportGenerated ? "sapReports.regenerateReports" : "sapReports.generateReports"
                            ),
                            icon: "DOCUMENT",
                            disabled:
                                !selectedSapCheckpointCheckIds.length || isGeneratingReport || checksQuery.isFetching,
                            action: () => generateSapCheckReports(selectedSapCheckpointCheckIds)
                        }
                    ]}
                    actionSummary={t("sapReports.selectionSummary", {
                        documents: selectedDocumentCount,
                        tasks: selectedTaskCount
                    })}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={onChangePage}
                    onChangeRowPerPage={onChangeRowPerPage}
                    fields={fields}
                    allFields={allFields}
                    onChangeFields={onChangeFields}
                    onChangeAllFields={onChangeAllFields}
                    onDraggableColumn={onDraggableColumn}
                />
                <KlassappTable
                    columns={columns}
                    data={rows}
                    isShowCheckedColumn
                    menuActions={[]}
                    isLoading={checksQuery.isFetching}
                    fields={fields}
                    allFields={allFields}
                    orderField={orderField}
                    tableRef={tableRef}
                    emptyText={t("academics.sapChecks.empty")}
                    onClickRowItem={() => {}}
                    onChangeFields={onChangeFields}
                    onUpdateRowData={onUpdateRowData}
                    onUpdateTableData={onUpdateTableData}
                    onToggleCheckHeader={onToggleCheckHeader}
                    onClickSortColumn={onClickSortColumn}
                    onDraggableColumn={onDraggableColumn}
                    onChangeAllFields={onChangeAllFields}
                />
            </div>
            {isReport || isStudentDetail ? null : (
                <RunSapChecksPopup isVisible={isRunSapPopupVisible} onClose={() => setIsRunSapPopupVisible(false)} />
            )}
            <SignaturePopup
                isShow={signaturePopup.isVisible}
                onClose={() => signaturePopup.close()}
                title={t("academics.sapChecks.reviewTitle")}>
                <div className={styles.divider}></div>
                <div className={styles.reviewAction}>
                    <Button
                        className={cx(styles.actionBtn, styles.rejectBtn)}
                        onClick={() => submitReview("rejected")}
                        loading={isRejecting}>
                        <span>{t("common:action.reject")}</span>
                        <Icon icon="CROSS_CIRCLE" color="#df1642" className={styles.actionIcon} />
                    </Button>
                    <Button
                        className={cx(styles.actionBtn, styles.approveBtn)}
                        onClick={() => submitReview("approved")}
                        loading={isApproving}>
                        <span>{t("common:action.approve")}</span>
                        <Icon icon="CHECKMARK_CIRCLE_1" color="#18a957" className={styles.actionIcon} />
                    </Button>
                </div>
            </SignaturePopup>
        </div>
    )
}

export default KlassappTableHOC(SapChecksList)
